// Editor viewport — a self-contained WebGPU scene rendering whatever
// MapJSON the EditorState currently holds. Reuses the production MapArena
// + MapArenaView stack so WYSIWYG is real: what you see here is what the
// game loads when you hit Playtest.
//
// Dropped OrbitControls: the editor uses a free-fly camera (PointerLock +
// WASD + Q/E + Shift sprint) for first-person navigation. Click without
// drag picks objects; click-drag (LMB) or RMB locks the cursor and enters
// fly mode. F frames the current selection.
//
// Apply paths: instead of rebuilding the scene on every state notification,
// the viewport reads the commit kind:
//   structural → full rebuild via MapArena + MapArenaView (camera preserved)
//   transform  → walk JSON, write transforms onto live Object3Ds in place
//   visual     → re-resolve material on named ids only
//   meta       → no-op
//
// Selection highlight, gizmo, and helper layers are owned here.

import * as THREE from 'three';
import { PMREMGenerator, WebGPURenderer } from 'three/webgpu';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import RAPIER from '@dimforge/rapier3d-compat';
import { MapArena } from '../../sim/MapArena';
import { MapArenaView } from '../../render/MapArenaView';
import { preloadBundle, getBundle, type MapBundle } from '../../maps/registry';
import type { MapJSON, ObjectNode, Vec3, Transform } from '../../maps/MapJSON';
import { PostPipeline } from '../../render/post/PostPipeline';
import { preloadSpaceshipAssets } from '../../render/SpaceshipAssets';
import { FlyControls } from './FlyControls';
import { EditorGizmo } from './EditorGizmo';
import { SelectionHighlight } from './SelectionHighlight';
import type { EditorState, CommitOpts } from './EditorState';
import type { EditorTool } from './EditorTool';

export interface EditorViewportCallbacks {
  /** Click-pick on the viewport. Receives the picked json id, or null on miss.
   *  `additive` is true on shift / ctrl / meta-click for multi-select. */
  onPickObject?(id: string | null, additive: boolean): void;
  /** Drag-drop file received. */
  onFileDrop?(files: FileList): void;
}

export class EditorViewport {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: WebGPURenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly fly: FlyControls;
  private gizmo: EditorGizmo | null = null;
  private highlight: SelectionHighlight;
  private grid: THREE.GridHelper | null = null;
  private axes: THREE.AxesHelper | null = null;
  private lightHelpers: THREE.Object3D[] = [];
  private post: PostPipeline | null = null;
  private rapierWorld: RAPIER.World | null = null;
  private arena: MapArena | null = null;
  private view: MapArenaView | null = null;
  private currentBundleId: string = '';
  private loadChain: Promise<void> = Promise.resolve();
  private initialFrameDone = false;
  private raf = 0;
  private running = false;
  private initialized = false;
  private timeSec = 0;
  private lastFrameMs = performance.now();
  private raycaster = new THREE.Raycaster();
  private pointerNdc = new THREE.Vector2();
  private disposed = false;
  private cbs: EditorViewportCallbacks;
  private state: EditorState | null = null;
  private tool: EditorTool | null = null;
  private dragGate: { x: number; y: number; armed: boolean } | null = null;
  private dropOverlay: HTMLElement | null = null;
  private dragOverCount = 0;

  constructor(parent: HTMLElement, cbs: EditorViewportCallbacks = {}) {
    this.cbs = cbs;
    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    parent.appendChild(this.canvas);

    this.renderer = new WebGPURenderer({
      canvas: this.canvas,
      antialias: false,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x0b0d12, 1);

    const { clientWidth, clientHeight } = parent;
    this.camera = new THREE.PerspectiveCamera(
      55,
      Math.max(1, clientWidth) / Math.max(1, clientHeight),
      0.1,
      1000
    );
    this.camera.position.set(18, 14, 18);
    this.camera.lookAt(0, 0, 0);

    this.fly = new FlyControls(this.camera, this.canvas);

    // Basic key + fill lights — MapArenaView's lights add to these.
    const amb = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(amb);
    const dir = new THREE.DirectionalLight(0xfff5d8, 1.6);
    dir.position.set(-3.5, 6, 3);
    this.scene.add(dir);

    this.highlight = new SelectionHighlight();

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    this.canvas.addEventListener('dragenter', this.onDragEnter);
    this.canvas.addEventListener('dragover', this.onDragOver);
    this.canvas.addEventListener('dragleave', this.onDragLeave);
    this.canvas.addEventListener('drop', this.onDrop);
    addEventListener('resize', this.onResize);

    // Drop overlay sits on top of the canvas inside the viewport pane.
    this.dropOverlay = document.createElement('div');
    this.dropOverlay.className = 'ed-drop-overlay';
    this.dropOverlay.style.display = 'none';
    this.dropOverlay.textContent = 'Drop .glb / .hdr to import';
    parent.appendChild(this.dropOverlay);
  }

  async init(): Promise<void> {
    await this.renderer.init();
    const pmrem = new PMREMGenerator(this.renderer);
    const envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
    this.scene.environment = envRT.texture;
    pmrem.dispose();

    await RAPIER.init();
    this.rapierWorld = new RAPIER.World({ x: 0, y: -22, z: 0 });
    this.resizeToContainer();
    this.post = new PostPipeline(
      this.renderer, this.scene, this.camera,
      this.canvas.clientWidth || 1, this.canvas.clientHeight || 1
    );
    this.initialized = true;
  }

  /** Wire EditorState + EditorTool. The viewport subscribes to both
   *  internally; callers don't need to forward events. */
  bind(state: EditorState, tool: EditorTool): void {
    this.state = state;
    this.tool = tool;
    this.gizmo = new EditorGizmo(this.camera, this.canvas, this.scene, state, tool, {
      onDraggingChanged: (dragging) => { this.fly.enabled = !dragging; }
    });
    state.subscribeDoc((opts) => this.applyDoc(opts));
    state.subscribeSelection(() => this.applySelection());
    tool.subscribe(() => this.applyToolState());
    this.applyToolState();
  }

  applyDoc(opts: CommitOpts): void {
    if (!this.state) return;
    const s = this.state.get();
    if (s.bundleId !== this.currentBundleId || opts.kind === 'structural') {
      void this.loadBundle(s.bundleId, s.json);
      return;
    }
    if (opts.kind === 'transform') {
      this.applyTransformsFromJson(s.json, opts.syncedIds ?? []);
      return;
    }
    if (opts.kind === 'visual') {
      // Visual changes still need a rebuild for material swaps / color
      // edits — incremental refresh would require touching MapArenaView
      // material wiring per id. Defer that to a later pass; for now reuse
      // the structural rebuild but preserve the camera (which we already
      // do — initialFrameDone guards re-framing).
      void this.loadBundle(s.bundleId, s.json);
      return;
    }
    // 'meta' → no scene effect.
  }

  private applySelection(): void {
    if (!this.state || !this.view) return;
    const s = this.state.get();
    const objIds = s.selectionIds
      .filter((id) => id.startsWith('obj:'))
      .map((id) => id.slice(4));
    this.highlight.setSelection(objIds, this.view.objectById);
    this.gizmo?.setObjectsById(this.view.objectById);
    this.gizmo?.setSelection(objIds);
    // Apply hidden ids
    for (const [id, o] of this.view.objectById) {
      o.visible = !s.hiddenIds.has(id);
    }
  }

  private applyToolState(): void {
    if (!this.tool) return;
    const t = this.tool.get();
    // Switching away from 'control' force-unlocks the pointer so a stray
    // hotkey (e.g. Q while locked) doesn't strand the camera in fly mode.
    if (t.mode !== 'control' && this.fly.isLocked()) this.fly.unlock();
    if (t.showGrid && !this.grid) {
      this.grid = new THREE.GridHelper(40, 40, 0x2a3142, 0x1a1d28);
      (this.grid.material as THREE.Material).transparent = true;
      (this.grid.material as THREE.Material).opacity = 0.6;
      this.scene.add(this.grid);
    } else if (!t.showGrid && this.grid) {
      this.scene.remove(this.grid);
      (this.grid.material as THREE.Material).dispose?.();
      this.grid.geometry.dispose();
      this.grid = null;
    }
    if (t.showAxes && !this.axes) {
      this.axes = new THREE.AxesHelper(2);
      (this.axes.material as THREE.Material).depthTest = false;
      this.axes.renderOrder = 1000;
      this.scene.add(this.axes);
    } else if (!t.showAxes && this.axes) {
      this.scene.remove(this.axes);
      this.axes.geometry.dispose();
      this.axes = null;
    }
  }

  /** Load (or reload) a bundle by id. */
  async loadBundle(bundleId: string, json: MapJSON): Promise<void> {
    const next = this.loadChain.then(() => this.doLoadBundle(bundleId, json));
    this.loadChain = next.catch(() => { /* swallow so subsequent calls still run */ });
    return next;
  }

  private async doLoadBundle(bundleId: string, json: MapJSON): Promise<void> {
    if (this.disposed) return;
    if (!this.initialized) throw new Error('[EditorViewport] init() first');
    const isBundleSwap = bundleId !== this.currentBundleId;
    this.disposeMapResources();

    const baseBundle = getBundle(bundleId);
    if (!baseBundle) {
      throw new Error(`[EditorViewport] no bundle registered for id "${bundleId}"`);
    }
    await preloadBundle(bundleId);

    const bundle: MapBundle = {
      id: bundleId,
      json,
      behaviorLoaders: baseBundle.behaviorLoaders,
      materialLoaders: baseBundle.materialLoaders
    };

    if (bundle.json.behaviors?.some((b) => b.module === 'spaceshipTilt' || b.module === 'ionThrusters')) {
      await preloadSpaceshipAssets();
    }

    this.arena = new MapArena(this.rapierWorld!, bundle);
    this.view = new MapArenaView(this.arena, bundle);
    this.view.onObjectsPopulated = (ids) => {
      // Refresh selection highlight if any populated id is selected.
      if (!this.state || !this.view) return;
      const sel = this.state.get();
      const objIds = sel.selectionIds
        .filter((id) => id.startsWith('obj:')).map((id) => id.slice(4));
      for (const id of ids) {
        if (objIds.includes(id)) this.highlight.refresh(id, this.view.objectById);
      }
    };
    await this.view.initRenderBehaviors();
    this.scene.add(this.view.group);
    this.currentBundleId = bundleId;

    // Tag every id-bearing object with userData.editorId for raycast picks.
    for (const [id, obj] of this.view.objectById) {
      obj.userData.editorId = id;
      obj.traverse((c) => { (c.userData as { editorId?: string }).editorId = id; });
    }

    // Build light helpers so users can click directly on lights.
    this.rebuildLightHelpers();

    // Re-apply selection / gizmo against the new objectById map.
    this.applySelection();

    // Frame the camera only on the first load OR when bundle changed
    // explicitly. Subsequent commits (structural rebuilds caused by edits)
    // preserve the user's current camera.
    if (!this.initialFrameDone || isBundleSwap) {
      const r = Math.max(6, this.arena.radius);
      const target = new THREE.Vector3(0, Math.max(1, r * 0.15), 0);
      const dir = new THREE.Vector3(1, 0.55, 1).normalize();
      this.camera.position.copy(target).addScaledVector(dir, r * 1.6);
      this.camera.lookAt(target);
      this.initialFrameDone = true;
    }
  }

  private rebuildLightHelpers(): void {
    for (const h of this.lightHelpers) {
      this.scene.remove(h);
    }
    this.lightHelpers = [];
    if (!this.view) return;
    this.view.group.traverse((obj) => {
      if ((obj as THREE.DirectionalLight).isDirectionalLight) {
        const helper = new THREE.DirectionalLightHelper(obj as THREE.DirectionalLight, 1.5, 0xfacc15);
        this.scene.add(helper);
        this.lightHelpers.push(helper);
      } else if ((obj as THREE.PointLight).isPointLight) {
        const helper = new THREE.PointLightHelper(obj as THREE.PointLight, 0.5, 0xfacc15);
        this.scene.add(helper);
        this.lightHelpers.push(helper);
      }
    });
  }

  /** Walk the json and write any non-synced primitive transforms onto the
   *  live Object3Ds. Used for inspector-typed edits and undo of gizmo
   *  drags. */
  private applyTransformsFromJson(json: MapJSON, syncedIds: string[]): void {
    if (!this.view) return;
    const synced = new Set(syncedIds);
    const visit = (nodes: ObjectNode[] | undefined): void => {
      if (!nodes) return;
      for (const n of nodes) {
        if (n.id && !synced.has(n.id)) {
          const obj = this.view!.objectById.get(n.id);
          if (obj) applyTransformLive(obj, n.transform);
        }
        if (n.type === 'group') visit(n.children);
      }
    };
    visit(json.objects);
    // Refresh selection highlight transforms next frame via syncTransforms.
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameMs = performance.now();
    this.loop();
  }

  pause(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.canvas.style.display = 'none';
    if (this.dropOverlay) this.dropOverlay.style.display = 'none';
  }

  resume(): void {
    if (this.running) return;
    this.canvas.style.display = 'block';
    this.resizeToContainer();
    this.start();
  }

  /** Public hook for drag-drop targets that aren't on the canvas. */
  setDropOverlayVisible(v: boolean): void {
    if (this.dropOverlay) this.dropOverlay.style.display = v ? 'flex' : 'none';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('dragenter', this.onDragEnter);
    this.canvas.removeEventListener('dragover', this.onDragOver);
    this.canvas.removeEventListener('dragleave', this.onDragLeave);
    this.canvas.removeEventListener('drop', this.onDrop);
    removeEventListener('resize', this.onResize);
    this.fly.dispose();
    this.gizmo?.dispose();
    this.highlight.dispose();
    this.disposeMapResources();
    try { this.renderer.dispose?.(); } catch { /* older three builds */ }
    this.canvas.remove();
    this.dropOverlay?.remove();
  }

  private disposeMapResources(): void {
    if (this.view) {
      this.scene.remove(this.view.group);
      this.view.group.traverse((obj) => {
        const m = (obj as THREE.Mesh).material;
        if (m) {
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose?.());
          else m.dispose?.();
        }
        const g = (obj as THREE.Mesh).geometry;
        g?.dispose?.();
      });
    }
    this.view = null;
    this.arena = null;
    if (this.rapierWorld) {
      this.rapierWorld.free?.();
      this.rapierWorld = new RAPIER.World({ x: 0, y: -22, z: 0 });
    }
    for (const h of this.lightHelpers) this.scene.remove(h);
    this.lightHelpers = [];
    this.highlight.clear();
  }

  // ---------- main loop ----------

  private loop = (): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.loop);

    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastFrameMs) / 1000);
    this.lastFrameMs = now;
    this.timeSec += dt;

    this.fly.update(dt);
    if (this.view) {
      this.view.setActiveCamera(this.camera);
      this.view.stepRender(this.timeSec, dt, 1);
    }
    // Keep selection-highlight shells aligned with their source meshes
    // (gizmo moves the source mesh in place; outline must follow).
    this.highlight.syncTransforms();
    this.post?.render(dt);
  };

  private onResize = (): void => {
    this.resizeToContainer();
  };

  private resizeToContainer(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const w = Math.max(1, parent.clientWidth);
    const h = Math.max(1, parent.clientHeight);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.post?.setSize(w, h);
  }

  // ---------- input ----------

  private onPointerDown = (e: PointerEvent): void => {
    // Fly-mode (pointer lock) is gated to 'control' mode — every other mode
    // leaves the camera static so the transform gizmo never has to fight
    // pointer-lock. Right-click does nothing in editor.
    if (e.button !== 0) return;
    if (this.tool?.get().mode === 'control') {
      this.fly.lock();
      e.preventDefault();
      return;
    }
    this.dragGate = { x: e.clientX, y: e.clientY, armed: true };
  };

  private onPointerMove = (_e: PointerEvent): void => {
    // No fly-on-drag — the only way to enter pointer-lock is to click in
    // 'control' mode (handled in onPointerDown).
  };

  private onPointerUp = (e: PointerEvent): void => {
    const g = this.dragGate;
    this.dragGate = null;
    if (e.button !== 0 || !g) return;
    if (g.armed && !this.fly.isLocked()) {
      const additive = e.shiftKey || e.metaKey || e.ctrlKey;
      this.pickAt(e.clientX, e.clientY, additive);
    }
  };

  private pickAt(clientX: number, clientY: number, additive: boolean): void {
    if (!this.cbs.onPickObject) return;
    if (!this.view) {
      this.cbs.onPickObject(null, additive);
      return;
    }
    const rect = this.canvas.getBoundingClientRect();
    this.pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNdc.y = -(((clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const hits = this.raycaster.intersectObject(this.view.group, true);
    for (const hit of hits) {
      // Skip selection-highlight shells.
      if (hit.object.userData.outline) continue;
      const id = findAncestorId(hit.object);
      if (id) {
        this.cbs.onPickObject(id, additive);
        return;
      }
    }
    this.cbs.onPickObject(null, additive);
  }

  // ---------- drag-drop ----------

  private onDragEnter = (e: DragEvent): void => {
    e.preventDefault();
    this.dragOverCount += 1;
    this.setDropOverlayVisible(true);
  };
  private onDragOver = (e: DragEvent): void => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  private onDragLeave = (e: DragEvent): void => {
    e.preventDefault();
    this.dragOverCount = Math.max(0, this.dragOverCount - 1);
    if (this.dragOverCount === 0) this.setDropOverlayVisible(false);
  };
  private onDrop = (e: DragEvent): void => {
    e.preventDefault();
    this.dragOverCount = 0;
    this.setDropOverlayVisible(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      this.cbs.onFileDrop?.(e.dataTransfer.files);
    }
  };

  // ---------- utilities used by the screen ----------

  /** Compute the world-space target where a newly-spawned object should
   *  land — current selection's centroid, or 5m in front of the camera. */
  spawnTarget(): Vec3 {
    const s = this.state?.get();
    if (s && s.selectionIds.length > 0 && this.view) {
      const center = new THREE.Vector3();
      let count = 0;
      for (const id of s.selectionIds) {
        const objId = id.startsWith('obj:') ? id.slice(4) : id;
        const o = this.view.objectById.get(objId);
        if (o) {
          o.updateWorldMatrix(true, false);
          center.add(new THREE.Vector3().setFromMatrixPosition(o.matrixWorld));
          count += 1;
        }
      }
      if (count > 0) {
        center.multiplyScalar(1 / count);
        return [center.x, center.y, center.z];
      }
    }
    const fwd = new THREE.Vector3();
    this.camera.getWorldDirection(fwd);
    const p = this.camera.position.clone().addScaledVector(fwd, 5);
    return [p.x, p.y, p.z];
  }

  /** Compute the world-space centre + bounding-radius of the current
   *  primary selection — used by F-frame. */
  selectionBounds(): { center: THREE.Vector3; radius: number } | null {
    const s = this.state?.get();
    if (!s || s.selectionIds.length === 0 || !this.view) return null;
    const box = new THREE.Box3();
    let any = false;
    for (const id of s.selectionIds) {
      const objId = id.startsWith('obj:') ? id.slice(4) : id;
      const o = this.view.objectById.get(objId);
      if (!o) continue;
      box.expandByObject(o);
      any = true;
    }
    if (!any) return null;
    const center = new THREE.Vector3(); box.getCenter(center);
    const size = new THREE.Vector3(); box.getSize(size);
    const radius = Math.max(1, size.length() * 0.5);
    return { center, radius };
  }

  frameSelection(): boolean {
    const b = this.selectionBounds();
    if (!b) return false;
    this.fly.focusOn(b.center, b.radius);
    return true;
  }
}

function applyTransformLive(obj: THREE.Object3D, t: Transform | undefined): void {
  if (!t) {
    obj.position.set(0, 0, 0);
    obj.rotation.set(0, 0, 0);
    obj.scale.set(1, 1, 1);
    return;
  }
  if (t.p) obj.position.set(t.p[0], t.p[1], t.p[2]);
  if (t.r) obj.rotation.set(t.r[0], t.r[1], t.r[2]);
  if (t.s !== undefined) {
    if (typeof t.s === 'number') obj.scale.setScalar(t.s);
    else obj.scale.set(t.s[0], t.s[1], t.s[2]);
  }
}

function findAncestorId(o: THREE.Object3D | null): string | null {
  let cur: THREE.Object3D | null = o;
  while (cur) {
    const id = (cur.userData as { editorId?: string } | undefined)?.editorId;
    if (id) return id;
    cur = cur.parent;
  }
  return null;
}
